import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  handleInput = () => {
    this.#syncTechnicalSearchInput();
  };

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';
    this.#clearTechnicalSearchInput();

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #syncTechnicalSearchInput() {
    const hiddenSearchInput = this.querySelector('[data-mh-technical-search-query]');
    if (!(hiddenSearchInput instanceof HTMLInputElement)) return;

    const searchInput = this.refs.searchPageInput;
    const displayValue = searchInput.dataset.mhDisplayValue?.trim() ?? '';
    const rawValue = searchInput.dataset.mhRawValue ?? hiddenSearchInput.value;
    const value = searchInput.value.trim();

    hiddenSearchInput.value = value === displayValue ? rawValue : searchInput.value;
  }

  #clearTechnicalSearchInput() {
    const hiddenSearchInput = this.querySelector('[data-mh-technical-search-query]');
    if (hiddenSearchInput instanceof HTMLInputElement) hiddenSearchInput.value = '';
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
